import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { api, ApiError, otherParty, type QueueActiveCall, type QueueWaitingCall } from '../api/client';
import { useDeviceStore } from '../store/device';
import { displayName } from '../utils/display';

const REFETCH_MS = 3_000;

export function QueuePanel() {
  const qc = useQueryClient();
  const incomingCall = useDeviceStore(s => s.incomingCall);
  const activeCall = useDeviceStore(s => s.activeCall);

  const query = useQuery({
    queryKey: ['queue'],
    queryFn: () => api.getQueue(),
    refetchInterval: REFETCH_MS,
    refetchIntervalInBackground: false,
  });

  const reject = useMutation({
    mutationFn: (taskSid: string) => api.rejectCall(taskSid),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['queue'] }),
  });

  function onAccept() {
    // The auto-ring delivers the same call as an incoming Voice SDK call.
    // Clicking Accept here is equivalent to clicking Accept in the modal.
    if (incomingCall) incomingCall.accept();
  }

  return (
    <div className="bg-white border border-slate-200 rounded-xl p-4">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-semibold text-slate-900">Call queue</h3>
        <span className="text-xs text-slate-400">refreshes every 3s</span>
      </div>

      {query.isLoading && <p className="mt-3 text-xs text-slate-500">Loading…</p>}
      {query.error && <p className="mt-3 text-xs text-red-700">Failed to load queue.</p>}

      {query.data && (
        <>
          <Section title="Active" empty="No active calls.">
            {query.data.active.map(c => <ActiveRow key={c.taskSid} call={c} />)}
          </Section>
          <Section title="Waiting" empty="No customers waiting.">
            {query.data.waiting.map(c => (
              <WaitingRow
                key={c.taskSid}
                call={c}
                canAccept={!activeCall && !!incomingCall}
                onAccept={onAccept}
                onReject={() => reject.mutate(c.taskSid)}
                rejecting={reject.isPending}
              />
            ))}
          </Section>
        </>
      )}
    </div>
  );
}

function Section({
  title, empty, children,
}: { title: string; empty: string; children: React.ReactNode }) {
  const items = Array.isArray(children) ? children.flat().filter(Boolean) : [children];
  return (
    <div className="mt-3">
      <h4 className="text-[10px] uppercase tracking-wider text-slate-400 mb-1.5">{title}</h4>
      {items.length === 0
        ? <p className="text-xs text-slate-400 italic">{empty}</p>
        : <ul className="space-y-1.5">{children}</ul>}
    </div>
  );
}

function ActiveRow({ call }: { call: QueueActiveCall }) {
  const [elapsed, setElapsed] = useState(0);
  useEffect(() => {
    const start = new Date(call.startedAt).getTime();
    const tick = () => setElapsed(Math.floor((Date.now() - start) / 1000));
    tick();
    const id = window.setInterval(tick, 1000);
    return () => window.clearInterval(id);
  }, [call.startedAt]);
  const number = otherParty(call);
  const arrow = call.direction === 'outbound' ? '→' : '←';
  const agentLabel = displayName({
    email: call.agentEmail,
    firstName: call.agentFirstName,
    lastName: call.agentLastName,
  });
  return (
    <li className="flex items-center gap-2 text-sm">
      <span className="inline-block w-2 h-2 rounded-full bg-red-500" aria-hidden />
      <span className="font-mono text-slate-700">{formatNumber(number)}</span>
      <span className="flex-1 text-xs text-slate-500 truncate" title={call.agentEmail}>{arrow} {agentLabel}</span>
      {call.direction === 'outbound' && (
        <span className="text-[10px] uppercase tracking-wider text-slate-400">out</span>
      )}
      <span className="text-xs font-mono text-slate-500">{formatDuration(elapsed)}</span>
    </li>
  );
}

function WaitingRow({
  call, canAccept, onAccept, onReject, rejecting,
}: {
  call: QueueWaitingCall;
  canAccept: boolean;
  onAccept: () => void;
  onReject: () => void;
  rejecting: boolean;
}) {
  const [waited, setWaited] = useState(0);
  useEffect(() => {
    const queued = new Date(call.queuedAt).getTime();
    const tick = () => setWaited(Math.floor((Date.now() - queued) / 1000));
    tick();
    const id = window.setInterval(tick, 1000);
    return () => window.clearInterval(id);
  }, [call.queuedAt]);
  return (
    <li className="flex items-center gap-2 text-sm">
      <span className="inline-block w-2 h-2 rounded-full bg-amber-500 animate-pulse" aria-hidden />
      <span className="font-mono text-slate-700">{formatNumber(call.from)}</span>
      <span className="flex-1 text-xs text-slate-500">waiting {formatDuration(waited)}</span>
      <button
        onClick={onAccept}
        disabled={!canAccept}
        className="text-xs px-2 py-1 rounded bg-emerald-100 text-emerald-800 hover:bg-emerald-200 disabled:opacity-50 disabled:cursor-not-allowed"
      >
        Accept
      </button>
      <button
        onClick={onReject}
        disabled={rejecting}
        className="text-xs px-2 py-1 rounded bg-red-100 text-red-800 hover:bg-red-200 disabled:opacity-50"
      >
        {rejecting ? '…' : 'Reject'}
      </button>
    </li>
  );
}

function formatNumber(raw: string): string {
  if (!raw) return '—';
  if (raw.startsWith('+61') && raw.length === 12) {
    return `${raw.slice(0, 3)} ${raw.slice(3, 6)} ${raw.slice(6, 9)} ${raw.slice(9)}`;
  }
  if (raw.startsWith('+1') && raw.length === 12) {
    return `${raw.slice(0, 2)} (${raw.slice(2, 5)}) ${raw.slice(5, 8)}-${raw.slice(8)}`;
  }
  return raw;
}

function formatDuration(seconds: number): string {
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return `${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`;
}

// Type-only re-export to keep ApiError reachable for any future error-rendering helper.
void ApiError;
